import { subjectPhotoGradient } from "Components/WebsiteTheme/subjectPhoto.style";
import styled, { keyframes } from "styled-components";
import { C, MQ } from "Components/WebsiteTheme/WebsiteTheme.style";

/* ── Hero ──────────────────────────────────────────────────────────── */

export const HomeHero = styled.section`
	/* Home only: the photo also fades into the page on its right edge. The tagline and its dark
	   contrast patch move in from the edge so they stay on the photo, not on the fade. */
	.sm_hero_photo img {
		object-position: center 42%;
	}

	${MQ.STACK} {
		.sm_hero_copy {
			order: -1;
		}
	}

	@media (min-width: 1200px) {
		.sm_hero_photo {
			/* Fade the entire photo panel, including overlays, to hide edge seams. */
			-webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 32px, #000 calc(100% - 32px), transparent 100%);
			mask-image: linear-gradient(90deg, transparent 0%, #000 32px, #000 calc(100% - 32px), transparent 100%);

			&::before {
				background: linear-gradient(
					90deg,
					#f7f9ff 0%,
					rgba(247, 249, 255, 0.9) 12%,
					rgba(247, 249, 255, 0.35) 30%,
					rgba(247, 249, 255, 0) 48%,
					rgba(247, 249, 255, 0) 80%,
					rgba(247, 249, 255, 0.55) 91%,
					#f7f9ff 100%
				);
			}

			&::after {
				background: radial-gradient(ellipse at 68% 88%, rgba(11, 28, 82, 0.6) 0%, rgba(11, 28, 82, 0) 60%);
			}
		}

		.sm_hero_tagline {
			right: 22%;
		}
	}

	.hh_highlights {
		display: grid;
		grid-template-columns: repeat(3, max-content);
		justify-content: start;
		gap: 28px;
		margin-top: 12px;

		li {
			display: flex;
			align-items: center;
			gap: 12px;
		}

		.sm_icon {
			width: 44px;
			height: 44px;

			svg {
				width: 22px;
				height: 22px;
			}
		}

		.sm_label {
			max-width: 150px;
			font-size: 14px;
		}

		${MQ.PHONE} {
			grid-template-columns: 1fr;
			gap: 14px;

			.sm_label {
				max-width: none;
			}
		}
	}
`;

/* ── How Subjects Master Works ─────────────────────────────────────── */

const playPulse = keyframes`
	0% {
		box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
	}
	70%,
	100% {
		box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
	}
`;

export const HowItWorksSection = styled.section`
	scroll-margin-top: 84px;

	.hw_grid {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 40px;
		margin-top: 48px;

		${MQ.STACK} {
			gap: 24px;
		}

		${MQ.TABLET} {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		${MQ.PHONE} {
			grid-template-columns: 1fr;
			gap: 16px;
			margin-top: 32px;
		}
	}

	.hw_item {
		position: relative;
	}

	/* Chevron between the steps (desktop row only) */
	.hw_next {
		position: absolute;
		top: 50%;
		right: -32px;
		width: 24px;
		height: 24px;
		color: ${C.navy};
		transform: translateY(-50%);

		${MQ.STACK} {
			display: none;
		}
	}

	.hw_card {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
		height: 100%;
		padding: 32px 24px 28px;
		border-width: 1px;
		border-style: solid;
		border-radius: 16px;
		text-align: center;

		${MQ.PHONE} {
			flex-direction: row;
			align-items: flex-start;
			gap: 16px;
			padding: 20px;
			text-align: left;
		}
	}

	.hw_num {
		position: absolute;
		top: 16px;
		left: 16px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		border-radius: 50%;
		color: #fff;
		font-size: 15px;
		font-weight: 700;

		${MQ.PHONE} {
			position: static;
			flex-shrink: 0;
			order: -1;
		}
	}

	.hw_card .sm_icon {
		background: #fff;
		box-shadow: 0 8px 20px -12px rgba(20, 42, 110, 0.35);

		${MQ.PHONE} {
			display: none;
		}
	}

	.hw_copy {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.hw_action {
		display: flex;
		justify-content: center;
		margin-top: 40px;
	}

	/* The site's one video button: orange, larger, white play badge with a soft pulse */
	.hw_video_btn {
		min-height: 60px;
		padding: 16px 34px 16px 18px;
		gap: 14px;
		border-radius: 12px;
		font-size: 17px;

		.sm_btn_play {
			position: relative;
			width: 36px;
			height: 36px;
			background: #fff;
			color: #d9480f;

			svg {
				width: 14px;
				height: 14px;
			}

			&::after {
				content: "";
				position: absolute;
				inset: 0;
				border-radius: 50%;
				box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
				animation: ${playPulse} 2.2s ease-out infinite;
			}
		}

		&:hover {
			box-shadow: 0 16px 28px -12px rgba(214, 72, 14, 0.85);
		}

		@media (prefers-reduced-motion: reduce) {
			.sm_btn_play::after {
				animation: none;
			}
		}

		${MQ.PHONE} {
			width: 100%;
			padding-right: 22px;
		}
	}
`;

/* ── Classroom Pilot band ──────────────────────────────────────────── */

export const PilotBandSection = styled.section`
	position: relative;
	overflow: hidden;
	background: ${C.band};

	/* Photo is the first column of the content grid, so it starts on the header/footer content line.
	   Landscape (the reference photo is a wide group shot), trimmed a little on the right. */
	.pb_photo {
		position: relative;
		aspect-ratio: 16 / 9;
		overflow: hidden;
		border-radius: 20px;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			object-position: center 35%;
		}

		/* Fade the photo into the section colour on the right edge only. */
		&::before {
			content: "";
			position: absolute;
			inset: 0;
			z-index: 1;
			background: linear-gradient(90deg, rgba(20, 42, 110, 0) 0%, rgba(20, 42, 110, 0) 78%, rgba(20, 42, 110, 0.45) 90%, #142a6e 100%);
			pointer-events: none;
		}

		${MQ.STACK} {
			grid-column: 1 / -1;
			aspect-ratio: 21 / 9;

			img {
				object-position: center 32%;
			}
		}

		${MQ.PHONE} {
			aspect-ratio: 16 / 10;
			border-radius: 16px;
		}
	}

	.pb_inner {
		position: relative;
		display: grid;
		grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr) 1px 300px;
		gap: 40px;
		align-items: center;
		padding-top: 72px;
		padding-bottom: 72px;

		${MQ.WIDE} {
			grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) 1px 270px;
			gap: 32px;
		}

		${MQ.STACK} {
			grid-template-columns: minmax(0, 1fr) 1px minmax(0, 0.9fr);
			padding-top: 48px;
			padding-bottom: 56px;
		}

		${MQ.TABLET} {
			grid-template-columns: 1fr;
			gap: 32px;
		}
	}

	.pb_copy {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 16px;

		${MQ.TABLET} {
			order: -1;
		}

		.sm_btn {
			margin-top: 8px;
		}
	}

	.pb_pill {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 7px 14px;
		border-radius: 999px;
		background: #fff;
		color: ${C.purple};
		font-size: 14px;
		font-weight: 700;

		svg {
			width: 16px;
			height: 16px;
		}
	}

	.pb_divider {
		align-self: stretch;
		background: rgba(255, 255, 255, 0.22);

		${MQ.TABLET} {
			display: none;
		}
	}

	.pb_pillars {
		display: flex;
		flex-direction: column;
		gap: 24px;

		li {
			display: flex;
			align-items: center;
			gap: 16px;
		}

		.sm_icon {
			background: #fff;
		}

		.pb_pillar_copy {
			display: flex;
			flex-direction: column;
			gap: 4px;
		}
	}
`;

/* ── Subjects ──────────────────────────────────────────────────────── */

export const HomeSubjectsSection = styled.section`
	.hs_row {
		display: grid;
		grid-template-columns: repeat(7, minmax(0, 1fr)) auto;
		align-items: stretch;
		gap: 14px;
		margin-top: 40px;

		${MQ.STACK} {
			grid-template-columns: repeat(4, minmax(0, 1fr));
		}

		${MQ.PHONE} {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 12px;
		}
	}

	.hs_tile {
		position: relative;
		display: block;
		width: 100%;
		aspect-ratio: 10 / 11;
		overflow: hidden;
		border: 0;
		border-radius: 12px;
		padding: 0;
		background: ${C.navy};
		box-shadow: 0 10px 24px -16px rgba(20, 42, 110, 0.6);
		font-family: inherit;
		cursor: pointer;
		transition: transform 0.35s ${C.ease}, box-shadow 0.35s ${C.ease};

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			transition: transform 0.6s ${C.ease};
		}

		${subjectPhotoGradient}

		&:hover {
			transform: translateY(-4px);
			box-shadow: 0 18px 32px -16px rgba(20, 42, 110, 0.6);

			img {
				transform: scale(1.06);
			}
		}

		&:focus-visible {
			outline: 3px solid ${C.blue};
			outline-offset: 3px;
		}

		${MQ.STACK} {
			aspect-ratio: 4 / 3;
		}

		${MQ.PHONE} {
			aspect-ratio: 1 / 1;
		}
	}

	/* Seventh tile spans two columns on the 4- and 2-column layouts, so no gap is left in the grid */
	.hs_cell--wide {
		${MQ.STACK} {
			grid-column: span 2;
		}
	}

	.hs_cell--wide .hs_tile {
		${MQ.STACK} {
			aspect-ratio: auto;
			height: 100%;
		}

		${MQ.PHONE} {
			aspect-ratio: 2 / 1;
			height: auto;
		}

		/* Fill the row height set by the other tiles instead of the photo's own height */
		img {
			${MQ.STACK} {
				position: absolute;
				inset: 0;
			}
		}
	}

	.hs_label {
		position: absolute;
		right: 10px;
		bottom: 12px;
		left: 12px;
		z-index: 1;
		color: #fff;
		font-size: 16px;
		font-weight: 700;
		line-height: 1.2;
		text-align: left;

		${MQ.PHONE} {
			font-size: 15px;
		}
	}

	.hs_action {
		align-self: center;
		padding-left: 18px;

		${MQ.STACK} {
			grid-column: 1 / -1;
			display: flex;
			justify-content: center;
			padding: 16px 0 0;
		}
	}
`;

/* ── Designed for the Classroom ────────────────────────────────────── */

export const ShowcaseSection = styled.section`
	.cs_grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 32px;
		margin-top: 48px;

		${MQ.STACK} {
			grid-template-columns: 1fr;
			gap: 40px;
			max-width: 760px;
			margin-right: auto;
			margin-left: auto;
		}
	}

	.cs_item {
		display: grid;
		grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
		align-items: center;
		gap: 20px;

		${MQ.PHONE} {
			grid-template-columns: 1fr;
		}
	}

	.cs_copy {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;

		.sm_icon {
			width: 48px;
			height: 48px;

			svg {
				width: 24px;
				height: 24px;
			}
		}
	}

	/* Laptop frame drawn in CSS */
	.cs_laptop {
		position: relative;
		padding: 0 6%;
		transition: transform 0.4s ${C.ease};
	}

	.cs_item:hover .cs_laptop {
		transform: translateY(-4px);
	}

	.cs_screen {
		position: relative;
		aspect-ratio: 16 / 10;
		overflow: hidden;
		border: 7px solid #1c2236;
		border-bottom-width: 9px;
		border-radius: 10px 10px 0 0;
		background: #fff;

		img,
		svg {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.cs_base {
		position: relative;
		height: 12px;
		margin: 0 -6%;
		border-radius: 0 0 12px 12px;
		background: linear-gradient(180deg, #e3e7f0 0%, #b9c0d0 100%);
		box-shadow: 0 16px 24px -14px rgba(20, 42, 110, 0.55);

		&::before {
			content: "";
			position: absolute;
			top: 0;
			left: 50%;
			width: 18%;
			height: 5px;
			border-radius: 0 0 6px 6px;
			background: #a7afc2;
			transform: translateX(-50%);
		}
	}
`;

/* ── Plans ─────────────────────────────────────────────────────────── */

export const PlansSection = styled.section`
	.pl_row {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
		align-items: stretch;
		gap: 20px;
		margin-top: 40px;

		${MQ.STACK} {
			grid-template-columns: 1fr;
			max-width: 640px;
			margin-right: auto;
			margin-left: auto;
		}
	}

	.pl_card {
		display: flex;
		align-items: center;
		gap: 16px;
		height: 100%;
		padding: 20px 20px 20px 22px;
		border-width: 1px;
		border-style: solid;
		border-radius: 16px;
		color: inherit;
		transition: transform 0.35s ${C.ease}, box-shadow 0.35s ${C.ease};

		&:hover {
			transform: translateY(-4px);
			box-shadow: ${C.cardShadowHover};
		}

		&:hover .pl_chev {
			transform: translateX(3px);
		}

		&:focus-visible {
			outline: 3px solid ${C.blue};
			outline-offset: 3px;
		}

		.sm_icon {
			border-radius: 14px;
		}
	}

	.pl_copy {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 4px;
	}

	.pl_title--blue {
		color: ${C.blue};
	}

	.pl_title--green {
		color: ${C.greenText};
	}

	.pl_title--purple {
		color: ${C.purple};
	}

	.pl_chev {
		flex-shrink: 0;
		width: 22px;
		height: 22px;
		color: ${C.navy};
		transition: transform 0.25s ${C.ease};
	}

	.pl_action {
		display: flex;
		align-items: center;
		padding-left: 8px;

		${MQ.STACK} {
			justify-content: center;
			padding: 8px 0 0;
		}
	}
`;
